/**
 * Shared role ID constants and types used by both the frontend and backend.
 * Single source of truth for canonical role identifiers (e.g. `super_admin`).
 * Update whenever roles are added/removed, renamed, or their IDs change.
 */

export const ROLES = {
  SUPER_ADMIN: 'super_admin',
  ADMIN: 'admin',
  TEACHER: 'teacher',
  USER: 'user',
  // Marketing affiliate: an external partner with a login that sees only the
  // performance of their own ?ref= links. Strictly separated from staff roles —
  // it grants exactly one capability and one route.
  AFFILIATE: 'affiliate',
  // Academic director: a senior subject expert who vets the content of one
  // subject at the levels their access code names, and nothing else. Holds
  // exactly one capability and one route family (`content:vet`, `/vet`).
  ACADEMIC_DIRECTOR: 'academic_director',
} as const;

export type RoleId = (typeof ROLES)[keyof typeof ROLES];

export const ALL_ROLES: readonly RoleId[] = Object.freeze(
  Object.values(ROLES) as RoleId[]
);

/**
 * Roles that staff may assign through access-code and user-management flows.
 * SUPER_ADMIN stays owner-only and can never be granted by those surfaces;
 * promotion goes through the audited operator CLI
 * (`npm run super-admin:promote`, server/scripts/tools/promote-super-admin.ts).
 *
 * This is deliberately separate from which users an ordinary admin may list:
 * AFFILIATE and ACADEMIC_DIRECTOR are assignable, but they are outside the
 * tutoring product and are not visible in the ordinary-admin user directory.
 */
export const ASSIGNABLE_ROLES = Object.freeze([
  ROLES.USER,
  ROLES.TEACHER,
  ROLES.ADMIN,
  ROLES.AFFILIATE,
  ROLES.ACADEMIC_DIRECTOR,
] as const satisfies readonly RoleId[]);
